/**
 * Transition update normalization utilities
 * 
 * Frontend workaround for backend issue where null values are ignored.
 * Backend only saves if isinstance(value, dict), so null values are silently ignored.
 * 
 * Workaround: Always send objects {}, never null
 */

/**
 * Check if a value is a plain object (not null, not array, not string, not Date, etc.)
 * More strict than typeof === 'object' check
 */
function isPlainObject(value: any): boolean {
  return value !== null &&
         typeof value === 'object' &&
         !Array.isArray(value) &&
         Object.prototype.toString.call(value) === '[object Object]' &&
         !(value instanceof Date) &&
         !(value instanceof RegExp);
}

/**
 * Normalize transition update data to ensure JSON fields are always objects
 * 
 * @param data - Transition update payload
 * @returns Normalized payload with null values converted to empty objects
 */
export function normalizeTransitionUpdate(data: any): any {
  const normalized = { ...data };
  
  // Ensure JSON fields are always objects, never null/undefined
  if ('state_parameters' in normalized) {
    normalized.state_parameters = normalizeJsonField(normalized.state_parameters);
    // Warn if we had to convert an invalid type
    if (normalized.state_parameters !== data.state_parameters && data.state_parameters != null) {
      console.warn('[normalizeTransitionUpdate] state_parameters must be an object, converted to empty object', {
        original: data.state_parameters,
        type: typeof data.state_parameters,
        isArray: Array.isArray(data.state_parameters)
      });
    }
  }
  
  if ('validation_rules' in normalized) {
    normalized.validation_rules = normalizeJsonField(normalized.validation_rules);
    // Warn if we had to convert an invalid type
    if (normalized.validation_rules !== data.validation_rules && data.validation_rules != null) {
      console.warn('[normalizeTransitionUpdate] validation_rules must be an object, converted to empty object', {
        original: data.validation_rules,
        type: typeof data.validation_rules,
        isArray: Array.isArray(data.validation_rules)
      });
    }
  }
  
  // Remove undefined values (don't send fields we're not updating)
  Object.keys(normalized).forEach(key => {
    if (normalized[key] === undefined) {
      delete normalized[key];
    }
  });
  
  return normalized;
}

/**
 * Normalize a JSON field to ensure it's always an object (never null, undefined, array, or string)
 * 
 * @param value - The value to normalize
 * @returns An object (empty {} if value is null/undefined/invalid)
 */
export function normalizeJsonField(value: any): Record<string, any> {
  // If it's already a valid plain object, return it
  if (isPlainObject(value)) {
    return value;
  }
  
  // Convert null, undefined, arrays, strings, dates, etc. to empty object
  return {};
}

/**
 * Check if a value is a valid JSON object (not null, not array, not string)
 * Uses isPlainObject for stricter checking
 */
export function isValidJsonObject(value: any): boolean {
  return isPlainObject(value);
}
